import { useRecordContext, useGetList, Loading, useTranslate } from 'react-admin';
import {
  Box,
  Typography,
  Chip,
  List as MuiList,
  ListItem,
  ListItemText,
  ListItemIcon,
  Tooltip,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import GroupIcon from '@mui/icons-material/Group';
import { ChannelTypeIcon } from './ChannelTypeIcon';
import { ChannelType, type Contact, type ContactIdentity } from '../../../types/chat.types';

/**
 * Extracts the local part before @ from a JID string.
 * For individual JIDs (e.g. "5511999998888@s.whatsapp.net") returns the phone number.
 * For group JIDs (e.g. "123456789-1234567890@g.us") returns the group ID.
 * Returns null if no @ is found.
 */
const extractJidLocalPart = (jid: string | null | undefined): string | null => {
  if (!jid) return null;
  const atIndex = jid.indexOf('@');
  return atIndex > 0 ? jid.substring(0, atIndex) : null;
};

/**
 * Returns the best human-readable identifier for a contact identity.
 * For Zapy/WhatsApp individuals: extracts the phone number from profileData.jid.
 * For Zapy/WhatsApp groups: extracts the group ID from profileData.jid, or uses identity.name.
 * Falls back to externalId for other channels.
 */
const getDisplayIdentifier = (identity: ContactIdentity): string => {
  if (identity.channelType === ChannelType.ZAPY) {
    const jid = identity.profileData?.jid as string | undefined;
    const lid = identity.profileData?.lid as string | undefined;
    const localPart = extractJidLocalPart(jid) ?? extractJidLocalPart(lid);
    if (localPart) return localPart;
  }
  return identity.externalId;
};

/**
 * Returns a human-readable label describing what the externalId represents
 * for each channel type (e.g., "Phone Number" for WhatsApp, "Chat ID" for Telegram).
 */
const getExternalIdLabel = (channelType: ChannelType, isGroup: boolean, translate: ReturnType<typeof useTranslate>): string => {
  const key = `resources.contact-identity.externalIdLabels.${channelType}${isGroup ? '_group' : ''}`;
  const fallbackKey = `resources.contact-identity.externalIdLabels.${channelType}`;
  const result = translate(key);
  if (result && result !== key) return result;
  return translate(fallbackKey);
};

/**
 * Returns the translated channel type display name.
 */
const getChannelTypeLabel = (channelType: ChannelType, translate: ReturnType<typeof useTranslate>): string => {
  return translate(`resources.channel.types.${channelType}`);
};

export const ContactIdentities = () => {
  const record = useRecordContext<Contact>();
  const translate = useTranslate();

  const { data: identities, isLoading } = useGetList<ContactIdentity>(
    'contact-identity',
    {
      filter: { contactId_eq: record?.id },
      pagination: { page: 1, perPage: 50 },
      sort: { field: 'createdAt', order: 'DESC' },
    },
    { enabled: !!record?.id }
  );

  if (isLoading) return <Loading />;
  if (!identities?.length) {
    return (
      <Typography variant="body2" color="textSecondary">
        {translate('resources.contact-identity.empty')}
      </Typography>
    );
  }

  return (
    <MuiList dense disablePadding>
      {identities.map((identity) => {
        const channelLabel = getChannelTypeLabel(identity.channelType, translate);
        const externalIdLabel = getExternalIdLabel(identity.channelType, !!identity.isGroup, translate);
        const isGroup = !!identity.isGroup;

        return (
          <ListItem
            key={identity.id}
            sx={{
              borderRadius: 1,
              mb: 0.5,
              '&:hover': { bgcolor: 'action.hover' },
            }}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>
              <ChannelTypeIcon type={identity.channelType} />
            </ListItemIcon>
            <ListItemText
              primary={
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="body2" fontWeight={500}>
                    {identity.name || getDisplayIdentifier(identity)}
                  </Typography>
                  <Chip
                    label={channelLabel}
                    size="small"
                    variant="outlined"
                    sx={{ height: 20, fontSize: '0.7rem' }}
                  />
                  {isGroup && (
                    <Tooltip title={translate('resources.contact-identity.group')}>
                      <GroupIcon fontSize="small" color="action" />
                    </Tooltip>
                  )}
                  {!isGroup && (
                    <Tooltip title={translate('resources.contact-identity.individual')}>
                      <PersonIcon fontSize="small" color="action" sx={{ opacity: 0.5 }} />
                    </Tooltip>
                  )}
                </Box>
              }
              secondary={
                <Box component="span" display="flex" flexDirection="column" gap={0.25} mt={0.5}>
                  <Typography variant="caption" color="textSecondary" component="span">
                    {externalIdLabel}: <Typography variant="caption" component="span" fontFamily="monospace">{getDisplayIdentifier(identity)}</Typography>
                  </Typography>
                </Box>
              }
            />
          </ListItem>
        );
      })}
    </MuiList>
  );
};
